import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getPaymentAdmin, type Payment } from "@/lib/api";
import { formatAmount, formatDate } from "@/lib/format";
import { ArrowLeft, Smartphone, CreditCard, User, Hash, Calendar } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { Badge, statusColor } from "@/components/DataTable";

type FullPayment = Payment & {
  livemode?: boolean;
  switch_request_id?: string | null;
  switch_response?: Record<string, unknown> | string | null;
  callback_url?: string | null;
};

export function PaymentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [payment, setPayment] = useState<FullPayment | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const p = (await getPaymentAdmin(id)) as FullPayment;
      setPayment(p);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erreur");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (loading) return <div className="card card-body text-[#7e8299]">Chargement…</div>;
  if (error || !payment) return <div className="card card-body text-danger-700">{error ?? "Paiement introuvable"}</div>;

  const isMobileMoney = ["mpesa", "orange", "airtel", "afrimoney"].includes(payment.payment_method);

  return (
    <div>
      <PageHeader
        title={
          <div className="flex items-center gap-3 flex-wrap">
            <span>Paiement</span>
            <Badge color={statusColor(payment.status)}>{payment.status}</Badge>
          </div>
        }
        breadcrumbs={[
          { label: "Transactions" },
          { label: "Paiements", to: "/payments" },
          { label: payment.id.slice(0, 14) + "…" },
        ]}
        actions={
          <button type="button" onClick={() => navigate(-1)} className="btn btn-light btn-sm">
            <ArrowLeft size={14} />
            Retour
          </button>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Main info card */}
        <div className="card lg:col-span-2">
          <div className="card-header">
            <div className="card-title">Informations</div>
          </div>
          <div className="card-body">
            <div className="flex items-baseline gap-2 mb-6">
              <div className="text-4xl font-bold text-[#181c32]">{formatAmount(payment.amount_minor, payment.currency)}</div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field icon={<Hash size={14} />} label="ID" value={payment.id} mono />
              <Field icon={<Hash size={14} />} label="Référence" value={payment.external_reference} mono />
              <Field
                icon={isMobileMoney ? <Smartphone size={14} /> : <CreditCard size={14} />}
                label="Méthode"
                value={payment.payment_method}
              />
              <Field icon={<Calendar size={14} />} label="Créé le" value={formatDate(payment.created_at)} />
              {payment.completed_at && (
                <Field icon={<Calendar size={14} />} label="Complété le" value={formatDate(payment.completed_at)} />
              )}
              <Field icon={<User size={14} />} label="Marchand" value={payment.merchant_id} mono />
            </div>

            {payment.failure_message && (
              <div
                className={`mt-6 rounded-lg border-l-4 px-4 py-3 ${
                  payment.status === "succeeded"
                    ? "bg-success-50 border-success-500"
                    : "bg-danger-50 border-danger-500"
                }`}
              >
                <div
                  className={`font-bold text-sm mb-1 ${
                    payment.status === "succeeded" ? "text-success-700" : "text-danger-700"
                  }`}
                >
                  {payment.status === "succeeded" ? "Message du switch" : "Échec du paiement"}
                </div>
                <div
                  className={`text-xs ${
                    payment.status === "succeeded" ? "text-success-700" : "text-danger-700"
                  }`}
                >
                  {payment.failure_message}
                </div>
                {payment.failure_code && (
                  <div
                    className={`text-xs mt-1 ${
                      payment.status === "succeeded" ? "text-success-700" : "text-danger-700"
                    }`}
                  >
                    Code: <span className="font-mono">{payment.failure_code}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Customer card */}
        <div className="card">
          <div className="card-header">
            <div className="card-title">Client</div>
          </div>
          <div className="card-body">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-brand-50 text-brand-700 flex items-center justify-center font-bold text-base">
                {[payment.customer_first_name, payment.customer_last_name]
                  .filter(Boolean)
                  .map((s) => s![0]?.toUpperCase() ?? "")
                  .join("") || "?"}
              </div>
              <div className="min-w-0">
                <div className="font-bold text-[#181c32] truncate">
                  {[payment.customer_first_name, payment.customer_last_name].filter(Boolean).join(" ") || "—"}
                </div>
                <div className="text-xs text-[#7e8299] truncate">{payment.customer_email ?? "Pas d'email"}</div>
              </div>
            </div>
            <div className="space-y-3">
              <Field label="Téléphone" value={payment.customer_phone ?? "—"} mono />
              <Field label="Email" value={payment.customer_email ?? "—"} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({
  icon,
  label,
  value,
  mono,
}: {
  icon?: React.ReactNode;
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-[#7e8299] font-bold mb-1.5 inline-flex items-center gap-1.5">
        {icon}
        {label}
      </div>
      <div className={`text-sm text-[#181c32] ${mono ? "font-mono break-all" : ""}`}>{value}</div>
    </div>
  );
}
